/**
 * Where a pre-login logo should send you.
 *
 * The rule is "a logo links to the home of the brand it depicts":
 *   • a school's logo on a school host  -> that school's public website
 *   • the Kaympus logo on the SaaS host -> the Kaympus marketing landing
 *
 * Most of the time a plain relative '/' is exactly right and no absolute URL
 * should be invented: auth and a school's public site are the SAME Next app
 * on the same host, and the root route is already host-aware — on a tenant
 * host '/' renders that school's landing, on the main host it renders the
 * Kaympus one (see app/(landing)/page.jsx).
 *
 * Three cases genuinely need more than '/':
 *
 *  1. saasOnly surfaces on a tenant host. /auth/superadmin-login and
 *     /auth/register-school are Kaympus-branded but are public on EVERY host
 *     (middleware whitelists them without a host check), so a relative '/'
 *     would point the Kaympus wordmark at a school's website.
 *
 *  2. The ?school=CODE preview. classifyHost() honours that query param on
 *     any host, so the page can show a SCHOOL's logo while sitting on the
 *     main domain — a bare '/' would then land on Kaympus marketing under
 *     that school's brand. The href must follow whatever chose the LOGO, not
 *     the hostname.
 *
 *  3. An unresolvable custom domain, where '/' provably dead-ends: the root
 *     route redirects to /domain-not-found.
 *
 * Window-only: getCurrentTenantContext() reads window.location and returns
 * null during SSR. Call these from an effect and seed state with '/', or the
 * server and client markup disagree on the very element about to be clicked.
 */
import { getCurrentTenantContext, getBaseSaaSDomain } from '@/utils/tenantResolver';

/** Absolute URL of the Kaympus SaaS landing; '/' when the base domain is unknown. */
export function getSaaSHomeUrl() {
  if (typeof window === 'undefined') return '/';
  const base = getBaseSaaSDomain();
  // Never emit "https://undefined" — NEXT_PUBLIC_BASE_DOMAINS is build-time
  // inlined and can legitimately be absent on a build host.
  if (!base) return '/';
  return `${window.location.protocol}//${base}`;
}

/**
 * @param {{saasOnly?: boolean, tenantResolved?: boolean}} opts
 *   saasOnly       this surface is Kaympus-branded and never tenant-scoped
 *   tenantResolved the tenant actually resolved from the backend
 */
export function getPreLoginHomeHref({ saasOnly = false, tenantResolved = true } = {}) {
  if (typeof window === 'undefined') return '/';
  const ctx = getCurrentTenantContext();
  const mode = ctx?.mode;

  if (saasOnly) {
    // Already on the SaaS origin -> relative. The ?school= preview param is
    // deliberately dropped: the destination is Kaympus, not a school.
    return mode === 'main' || mode === 'development' ? '/' : getSaaSHomeUrl();
  }
  if (mode === 'development' && ctx?.identifier) {
    return `/?school=${encodeURIComponent(ctx.identifier)}`;
  }
  if (mode === 'customDomain' && !tenantResolved) {
    return getSaaSHomeUrl();
  }
  return '/';
}
